import { Button, IconButton } from '@ads/components-react';

import { XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@/shared/ui/atoms';

import type { ExamPublication } from '../../../types/exam';

export interface ExpandDurationModalProps {
  readonly publication: ExamPublication | null;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSave: (data: { publicationId: number; newEndDate: string }) => void;
}

export function ExpandDurationModal({
  publication,
  open,
  onClose,
  onSave,
}: ExpandDurationModalProps) {
  const { t } = useTranslation('examManagement');
  const [newEndDate, setNewEndDate] = useState('');
  const [error, setError] = useState(false);

  const handleClose = () => {
    setNewEndDate('');
    setError(false);
    onClose();
  };

  const handleSave = () => {
    if (!publication) return;
    if (!newEndDate) {
      setError(true);
      return;
    }
    onSave({ publicationId: publication.id, newEndDate });
    setNewEndDate('');
    setError(false);
  };

  if (!publication) return null;

  return (
    <Dialog.Root open={open} onOpenChange={o => !o && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none"
          aria-describedby={undefined}
        >
          <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border px-md py-xs">
            <Dialog.Title className="text-ctx-content-title ts-subtitle-xs font-semibold">
              {t('publications.expandModal.title')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('actions.cancel')}
              />
            </Dialog.Close>
          </div>

          <div className="flex flex-col gap-md overflow-y-auto px-md py-md">
            <div className="flex flex-col gap-xs">
              <div className="flex flex-col gap-quark">
                <span className="ts-caption-xxs text-ctx-content-placeholder">
                  {t('publications.expandModal.examTitle')}
                </span>
                <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
                  {publication.lessonPlanTitle}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-xs">
                <div className="flex flex-col gap-quark">
                  <span className="ts-caption-xxs text-ctx-content-placeholder">
                    {t('publications.editModal.startDate')}
                  </span>
                  <span className="ts-paragraph-xs text-ctx-content-base">
                    {new Date(publication.startDate).toLocaleDateString('pt-BR')}
                  </span>
                </div>
                <div className="flex flex-col gap-quark">
                  <span className="ts-caption-xxs text-ctx-content-placeholder">
                    {t('publications.expandModal.currentEnd')}
                  </span>
                  <span className="ts-paragraph-xs text-ctx-content-base">
                    {new Date(publication.endDate).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-quark">
              <DatePicker
                label={t('publications.expandModal.newEnd')}
                value={newEndDate}
                onChange={v => {
                  setNewEndDate(v);
                  setError(false);
                }}
              />
              {error && (
                <span className="ts-caption-xxs text-negative-500">
                  {t('publications.expandModal.required')}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-xs border-t-hairline border-ctx-layout-border px-md py-xs">
            <Button hierarchy="secondary" size="sm" onClick={handleClose}>
              {t('actions.cancel')}
            </Button>
            <Button size="sm" onClick={handleSave}>
              {t('actions.save')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
